import { ReactNode, useState, useEffect, useCallback } from 'react'
import { useAuth as useClerkAuth, useUser } from '@clerk/clerk-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Menu, X, Sun, Moon, LogOut, ChevronDown, Sparkles } from 'lucide-react'
import Sidebar from './Sidebar'
import NotificationsDropdown from './NotificationsDropdown'
import HelpButton from '../help/HelpButton'
import { useHelpArticles } from '../help/useHelp'
import CommandPalette, { useCommandPaletteHotkey } from './CommandPalette'
import ProfileModal from './ProfileModal'
import AssistantDrawer from '../assistant/AssistantDrawer'
import AssistantPanel from '../assistant/AssistantPanel'
import { useToast } from '@comforthub/design-system'
import { useAuth } from '../../contexts/AuthContext'
import { useAssistantAccess } from '../../hooks/useAssistantAccess'
import { useAssistantLayout } from '../../lib/useAssistantLayout'
import { ASSISTANT_SHORTCUT_LABEL, useAssistantShortcut } from '../../lib/assistantShortcut'
import { subscribeAssistantGuide } from '../../lib/assistantGuide'
import { commandPaletteHint } from '../../lib/platformKey'
import { api } from '../../lib/api'
import {
  performBackofficeLogout,
  clearEntryContext,
  getEntryContext,
  setEntryContext,
} from '../../lib/entryContext'
import { whenLeaveAllowed } from '../../lib/leaveGuard'
import { useLiveEvents } from '../../hooks/useLiveEvents'
import { LiveStatusContext } from '../../contexts/LiveStatusContext'
import AutoPrintWatcher from '../orders/AutoPrintWatcher'

/** Routes whose content manages its own scrolling and should span the full
 *  width/height of the main area instead of the centered, max-width column. */
const FULL_BLEED_ROUTES = ['/orders/manage']

/** Routes that keep the centered max-width column but manage their own scroll
 *  on desktop (lg+), so they can split into independently-scrolling panes
 *  instead of growing the whole page into one long document scroll. */
const SELF_SCROLL_ROUTES = ['/shifts/generate']

export default function Shell({ children }: { children: ReactNode }) {
  const { user } = useUser()
  const { signOut } = useClerkAuth()
  const navigate = useNavigate()
  const {
    companyName,
    switchCompany,
    companyId,
    activeRole,
    setPreviewRole,
    availablePersonas,
    activePersona,
    impersonation,
    endImpersonation,
  } = useAuth()
  // Admin persona: Platform AI, for platform admins. Any other persona: the company's AI, which
  // needs assistant:use and the AI assistant module in its package, platform admins included.
  const { available: aiAvailable } = useAssistantAccess()
  // The one live order stream for the session (ADR-W12). Shell renders only once
  // /me has loaded; switching company or impersonating reloads the page.
  const liveStatus = useLiveEvents()
  const { pathname } = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  // Below 1024 px the AI is the drawer, opened on demand and never remembered.
  const [drawerOpen, setDrawerOpen] = useState(false)
  // Desktop: one layout, AI open or hidden (assistantLayout.ts).
  const {
    isDesktop,
    open: aiPanelOpen,
    setOpen: setAiPanelOpen,
    fullMenuWithAssistant,
    setFullMenuWithAssistant,
  } = useAssistantLayout({ pathname, enabled: aiAvailable })
  // Bumped when the user opens the AI, so the panel puts focus in its message box.
  const [aiFocusToken, setAiFocusToken] = useState(0)
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme')
      if (saved) return saved === 'dark'
      return true // Default to dark mode
    }
    return true
  })
  
  const [availableCompanies, setAvailableCompanies] = useState<{ id: string; name: string; role: string }[]>([])
  const [contextOpen, setContextOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [profileModalOpen, setProfileModalOpen] = useState(false)
  const toast = useToast()

  const showAssistant = aiAvailable
  const showSidePanel = showAssistant && isDesktop && aiPanelOpen

  const openAssistant = useCallback(() => {
    if (isDesktop) setAiPanelOpen(true)
    else setDrawerOpen(true)
    setAiFocusToken((n) => n + 1)
  }, [isDesktop, setAiPanelOpen])

  const hideAssistant = useCallback(() => {
    if (isDesktop) setAiPanelOpen(false)
    else setDrawerOpen(false)
  }, [isDesktop, setAiPanelOpen])

  const aiIsOpen = isDesktop ? aiPanelOpen : drawerOpen
  const toggleAssistant = useCallback(() => {
    if (aiIsOpen) hideAssistant()
    else openAssistant()
  }, [aiIsOpen, hideAssistant, openAssistant])

  // Ctrl/⌘ + . opens or hides the AI anywhere but another text field.
  useAssistantShortcut(showAssistant, toggleAssistant)

  useEffect(() => {
    let active = true
    api.get<any[]>('/me/companies')
      .then(res => {
        if (active) setAvailableCompanies(res.data || [])
      })
      .catch(err => console.error('Failed to load available companies', err))
    return () => {
      active = false
    }
  }, [companyId]) // reload when current company changes

  // The drawer belongs to small screens; crossing to desktop closes it.
  useEffect(() => {
    if (isDesktop) setDrawerOpen(false)
  }, [isDesktop])

  // Open the assistant when a checklist or wizard asks for a guided prompt.
  useEffect(() => {
    if (!aiAvailable) return
    return subscribeAssistantGuide(() => {
      if (isDesktop) setAiPanelOpen(true)
      else setDrawerOpen(true)
    })
  }, [aiAvailable, isDesktop, setAiPanelOpen])

  // Synchronize state with HTML element class list
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark')
      localStorage.setItem('theme', 'dark')
    } else {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('theme', 'light')
    }
  }, [darkMode])

  // Open on desktop, closed below 1024 px. Only crossing that width resets it:
  // phone browsers send resize when the address bar hides or shows on scroll,
  // and that used to close the menu the user had just opened.
  useEffect(() => {
    let wasDesktop: boolean | null = null
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024
      if (desktop === wasDesktop) return
      wasDesktop = desktop
      setSidebarOpen(desktop)
    }
    handleResize() // Set initial
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const userDisplayName = user?.fullName || user?.primaryEmailAddress?.emailAddress || 'User'
  const userInitials = userDisplayName.charAt(0).toUpperCase()

  // Personas the user actually holds — Merchant, Manager, Worker, Courier, Partner, Admin.
  // Only offer matching personas so a user can't preview a role they don't have.
  const availableRoles = availablePersonas

  const fullBleed = FULL_BLEED_ROUTES.includes(pathname)
  const selfScroll = SELF_SCROLL_ROUTES.includes(pathname)

  // Switching role or company, choosing again and logging out change the account before they
  // navigate, which can swap out a page with unsaved changes before the router could ask, so
  // each goes through whenLeaveAllowed.
  const applyPersona = (persona: string) => {
    setPreviewRole(persona)
    const prev = getEntryContext() ?? {}
    setEntryContext({
      ...prev,
      previewRole: persona,
      isPlatformAdmin: persona === 'Admin',
    })
    if (persona === 'Admin') navigate('/platform', { replace: true })
    else if (persona === 'Courier') navigate('/courier/deliveries', { replace: true })
    else if (persona === 'Partner') navigate('/partner/orders', { replace: true })
    else navigate('/dashboard', { replace: true })
  }

  const goToRoleSelector = () => {
    clearEntryContext()
    setPreviewRole(null)
    navigate('/entry')
  }

  const openPalette = useCallback(() => setPaletteOpen(true), [])
  useCommandPaletteHotkey(openPalette)
  // The palette's Help group: the articles the caller may read ([] without help).
  const { articles: helpArticles } = useHelpArticles()

  // The chip names where you are and who you are. The company part may
  // truncate; the role never does.
  const contextCompany = activePersona === 'Admin' ? 'Platform' : companyName
  const contextLabel = contextCompany
    ? `${contextCompany} · ${activeRole}`
    : `Role: ${activeRole}`

  // Desktop with the AI open: the menu is the icon column, unless the user asked
  // for the full menu beside the AI (this session). Its hide button then goes
  // back to icons instead of hiding the whole menu.
  const sidebarCompact = showSidePanel && !fullMenuWithAssistant
  const sidebarBesideAi = showSidePanel && fullMenuWithAssistant
  const headerMenuButton = !isDesktop || !sidebarOpen

  // The frame and <main> are `relative` on purpose: overflow-hidden only clips
  // absolutely positioned children when the box itself is positioned. Without
  // it an absolute element with no positioned parent (e.g. an sr-only table
  // caption) is placed against the page, the page becomes scrollable, and the
  // mouse wheel scrolls the whole app frame off screen.
  const frame = (
    <div
      className="relative flex h-dvh bg-background text-foreground overflow-hidden font-sans transition-colors duration-300"
      data-testid="app-shell"
    >
      
      {/* Sidebar Backdrop overlay for iPad/mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-slate-950/40 dark:bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar Navigation */}
      <Sidebar
        open={sidebarOpen}
        setOpen={setSidebarOpen}
        compact={sidebarCompact}
        onShowFullMenu={() => setFullMenuWithAssistant(true)}
        onCollapse={sidebarBesideAi ? () => setFullMenuWithAssistant(false) : undefined}
        collapseLabel={sidebarBesideAi ? 'Shrink menu to icons' : 'Collapse left menu'}
        assistant={
          showAssistant && isDesktop
            ? { open: aiPanelOpen, onToggle: toggleAssistant }
            : undefined
        }
      />

      {/* Desktop AI: beside the menu, before the page. Not an overlay, so Esc leaves it alone. */}
      {showSidePanel ? (
        <aside
          className="hidden lg:flex w-[380px] shrink-0 border-r border-border bg-surface flex-col h-full overflow-hidden"
          data-testid="assistant-side-panel"
          aria-label="AI assistant"
        >
          <AssistantPanel variant="side" active onClose={hideAssistant} focusToken={aiFocusToken} />
        </aside>
      ) : null}

      <div className="flex flex-col flex-1 min-w-0 h-full overflow-hidden">
        {impersonation ? (
          <div className="shrink-0 flex items-center justify-between gap-4 bg-amber-400 px-4 py-2 text-amber-950 text-sm">
            <span>
              Impersonating{' '}
              <strong>{impersonation.targetName ?? impersonation.targetUserId}</strong>
              {companyName ? ` · ${companyName}` : ''}
            </span>
            <button
              type="button"
              onClick={() => void endImpersonation()}
              className="rounded-lg bg-amber-950/10 hover:bg-amber-950/20 px-3 py-1 text-xs font-semibold"
            >
              Exit impersonation
            </button>
          </div>
        ) : null}
        {/* Modern minimalistic Header */}
        <header className="flex items-center justify-between gap-3 px-4 md:px-5 py-2 bg-surface/80 backdrop-blur-md border-b border-border shrink-0 z-30 transition-colors duration-300">
          {/* The menu button never shrinks and sits above the right-hand group,
              so nothing in the header can cover it on a narrow screen. */}
          <div className="relative z-10 flex items-center gap-3 shrink-0">
            {/* The menu has its own hide button on desktop; this one shows only
                below 1024 px and on desktop once the whole menu is hidden. */}
            {headerMenuButton ? (
              <button
                type="button"
                onClick={() => setSidebarOpen(o => !o)}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center text-foreground-muted hover:text-foreground rounded-lg hover:bg-surface-hover transition-all"
                aria-label={sidebarOpen ? 'Collapse left menu' : 'Expand left menu'}
                title={sidebarOpen ? 'Collapse left menu' : 'Expand left menu'}
              >
                {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
              </button>
            ) : null}
            {/* Desktop with the whole menu hidden: Ask AI lives here instead. */}
            {showAssistant && isDesktop && !sidebarOpen ? (
              <button
                type="button"
                data-testid="assistant-header-toggle"
                onClick={toggleAssistant}
                className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-all ${
                  aiPanelOpen ? 'bg-accent text-white hover:opacity-90' : 'text-accent hover:bg-surface-hover'
                }`}
                aria-label={aiPanelOpen ? 'Hide AI' : 'Ask AI'}
                title={`${aiPanelOpen ? 'Hide AI' : 'Ask AI'} (${ASSISTANT_SHORTCUT_LABEL})`}
                aria-keyshortcuts="Control+. Meta+."
              >
                <Sparkles size={16} aria-hidden="true" />
              </button>
            ) : null}
          </div>

          {/* Below 640 px the header holds only the menu, AI, bell and the chip.
              On a touch screen every button is at least 44 px wide (index.css),
              and with the theme, avatar and Log Out it was wider than a phone, so
              this group spilled left over the menu button. Theme and Log Out
              move into the chip's menu there. No justify-end: if the group ever
              overflows again it runs off the right edge, not over the menu. */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {/* Theme Toggle Button */}
            <button
              onClick={() => setDarkMode(d => !d)}
              className="hidden sm:inline-flex h-9 w-9 items-center justify-center rounded-lg text-foreground-muted hover:text-foreground hover:bg-surface-hover transition-all"
              title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {darkMode ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="hidden sm:flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-foreground-muted hover:text-foreground hover:bg-surface-hover transition-all text-[11px] font-medium"
              title={`Jump to page (${commandPaletteHint()})`}
              aria-label="Open command palette"
            >
              <span>Jump</span>
              <kbd className="font-mono text-[11px] border border-border rounded px-1 py-0.5">{commandPaletteHint()}</kbd>
            </button>

            {/* Under 1024 px the assistant opens from here, not from a floating
                button over the page. The ::before box widens the hit area to
                44 × 44 px without changing the header's height. */}
            {showAssistant && !isDesktop ? (
              <button
                type="button"
                data-testid="assistant-header-open"
                onClick={openAssistant}
                className="relative p-1.5 rounded-lg text-accent hover:bg-surface-hover transition-all before:absolute before:-inset-2 before:content-['']"
                aria-label="Open AI assistant"
                title="Open AI assistant"
              >
                <Sparkles size={16} />
              </button>
            ) : null}

            {/* "?": the help article for this page, when there is one */}
            <HelpButton />

            {/* Smart Notifications Bell Dropdown */}
            <NotificationsDropdown />

            {/* Separator (from 640 px; a phone needs the room for the chip) */}
            <div className="hidden sm:block h-6 w-px bg-border" />

            {/* Profile + context chip */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <div className="hidden sm:flex w-7 h-7 rounded-full bg-gradient-to-tr from-accent to-accent-strong items-center justify-center text-white font-semibold text-xs shadow-md ring-2 ring-accent/10">
                  {userInitials}
                </div>
                {/* Name and Edit are desktop-only; the chip below shows at every width. */}
                <div className="flex flex-col text-left min-w-0 justify-center">
                  <div className="hidden md:flex items-center gap-1.5 min-w-0">
                    <span
                      className="text-xs font-medium text-foreground leading-none truncate max-w-[8rem]"
                      title={user?.primaryEmailAddress?.emailAddress || undefined}
                    >
                      {user?.username || user?.firstName || 'Administrator'}
                    </span>
                    <button
                      onClick={() => setProfileModalOpen(true)}
                      className="text-xs font-medium text-slate-500 hover:text-accent transition-colors underline shrink-0"
                    >
                      Edit
                    </button>
                  </div>

                  <div className="relative md:mt-0.5">
                    <button
                      type="button"
                      onClick={() => setContextOpen((o) => !o)}
                      className="flex items-center gap-1 min-w-0 max-w-[8rem] sm:max-w-[11rem] md:max-w-[13rem] text-[10px] font-semibold bg-accent/15 hover:bg-accent/25 text-accent px-1.5 py-0.5 rounded-md transition-colors"
                      aria-expanded={contextOpen}
                      aria-haspopup="menu"
                      aria-label="Switch company or role"
                      title={contextLabel}
                    >
                      {contextCompany ? (
                        <>
                          <span className="truncate min-w-0">{contextCompany}</span>
                          <span className="shrink-0 whitespace-nowrap">· {activeRole}</span>
                        </>
                      ) : (
                        <span className="truncate">{contextLabel}</span>
                      )}
                      <ChevronDown size={10} className="shrink-0 mt-0.5" />
                    </button>
                    {contextOpen && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setContextOpen(false)} />
                        <div
                          role="menu"
                          className="absolute right-0 mt-1 w-56 rounded-xl bg-surface border border-border shadow-lg py-1.5 z-50 animate-in fade-in slide-in-from-top-1 duration-100"
                        >
                          {availableRoles.length > 0 && (
                            <>
                              <div className="px-3 py-1 text-[10px] font-bold text-foreground-muted uppercase tracking-wider">
                                Switch role
                              </div>
                              {availableRoles.map((r) => (
                                <button
                                  key={r}
                                  type="button"
                                  role="menuitem"
                                  onClick={() => {
                                    whenLeaveAllowed(() => applyPersona(r))
                                    setContextOpen(false)
                                  }}
                                  className={`w-full text-left px-3 py-1.5 text-xs transition-colors flex items-center justify-between ${
                                    r === activeRole
                                      ? 'bg-accent/10 text-accent font-semibold'
                                      : 'text-foreground hover:bg-surface-hover'
                                  }`}
                                >
                                  <span>{r}</span>
                                  {r === activeRole && <span className="text-[10px] font-bold">✓</span>}
                                </button>
                              ))}
                            </>
                          )}

                          {availableCompanies.length > 1 && activePersona !== 'Admin' && (
                            <>
                              <div className="my-1 border-t border-border" />
                              <div className="px-3 py-1 text-[10px] font-bold text-foreground-muted uppercase tracking-wider">
                                Switch company
                              </div>
                              {availableCompanies.map((c) => (
                                <button
                                  key={c.id}
                                  type="button"
                                  role="menuitem"
                                  onClick={() => {
                                    setContextOpen(false)
                                    if (c.id !== companyId) {
                                      whenLeaveAllowed(() => void switchCompany(c.id))
                                    }
                                  }}
                                  className={`w-full text-left px-3 py-2 text-xs transition-colors flex items-center justify-between gap-2 ${
                                    c.id === companyId
                                      ? 'bg-accent/10 text-accent font-semibold'
                                      : 'text-foreground hover:bg-surface-hover'
                                  }`}
                                >
                                  <div className="flex flex-col min-w-0 text-left">
                                    <span className="truncate">{c.name}</span>
                                    <span className="text-[11px] text-foreground-muted font-medium">
                                      {c.role === 'OWNER'
                                        ? 'Owner'
                                        : c.role === 'MANAGER'
                                          ? 'Manager'
                                          : 'Staff'}
                                    </span>
                                  </div>
                                  {c.id === companyId && <span className="text-[10px] font-bold shrink-0">✓</span>}
                                </button>
                              ))}
                            </>
                          )}

                          <div className="my-1 border-t border-border" />
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              setContextOpen(false)
                              whenLeaveAllowed(goToRoleSelector)
                            }}
                            className="w-full text-left px-3 py-1.5 text-xs text-foreground-muted hover:bg-surface-hover hover:text-accent transition-colors"
                          >
                            Choose company and role…
                          </button>

                          {/* Phones only: these two leave the header below 640 px. */}
                          <div className="sm:hidden">
                            <div className="my-1 border-t border-border" />
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                setContextOpen(false)
                                setDarkMode((d) => !d)
                              }}
                              className="w-full text-left px-3 py-1.5 text-xs text-foreground hover:bg-surface-hover transition-colors flex items-center gap-2"
                            >
                              {darkMode ? <Sun size={14} /> : <Moon size={14} />}
                              {darkMode ? 'Light mode' : 'Dark mode'}
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                setContextOpen(false)
                                whenLeaveAllowed(() => performBackofficeLogout(signOut))
                              }}
                              className="w-full text-left px-3 py-1.5 text-xs text-foreground hover:bg-surface-hover hover:text-rose-600 dark:hover:text-rose-400 transition-colors flex items-center gap-2"
                            >
                              <LogOut size={14} />
                              Log out
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => whenLeaveAllowed(() => performBackofficeLogout(signOut))}
                className="hidden sm:inline-flex h-9 w-9 items-center justify-center text-foreground-muted hover:text-rose-600 dark:hover:text-rose-400 hover:bg-surface-hover rounded-lg transition-all"
                title="Log Out"
              >
                <LogOut size={15} />
              </button>
            </div>
          </div>
        </header>

        {/* Main Area — centered/scrollable by default, full-bleed for board
            views, self-scroll for pages that split into panes on desktop. */}
        <main className={`relative flex-1 bg-background transition-colors duration-300 ${
          fullBleed
            ? 'overflow-hidden'
            : selfScroll
            ? 'overflow-y-auto lg:overflow-hidden'
            : 'overflow-y-auto p-3 md:p-4 xl:p-5'
        }`}>
          {fullBleed ? (
            <div className="h-full p-3 md:p-4 xl:p-5">
              {children}
            </div>
          ) : selfScroll ? (
            <div className="lg:h-full">
              {children}
            </div>
          ) : (
            <div className={`${showSidePanel ? 'max-w-none' : 'max-w-7xl mx-auto'} space-y-4`}>
              {children}
            </div>
          )}
        </main>
      </div>

      {/* Change User Data Modal */}
      <ProfileModal
        open={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        onSaved={() => toast.success('Profile data saved successfully!')}
      />

      {/* Below 1024 px: the drawer, opened from the header sparkle. */}
      {showAssistant && !isDesktop ? (
        <AssistantDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} focusToken={aiFocusToken} />
      ) : null}

      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        onChooseCompanyRole={() => whenLeaveAllowed(goToRoleSelector)}
        onToggleTheme={() => setDarkMode((d) => !d)}
        helpArticles={helpArticles}
      />
    </div>
  )

  // Sidebar and the routed page read it through useLiveStatus().
  return (
    <LiveStatusContext.Provider value={liveStatus}>
      {frame}
      {/* Prints new orders on a device that turned auto-print on (Board → Printer). */}
      <AutoPrintWatcher />
    </LiveStatusContext.Provider>
  )
}
